/**
 * The credit balance (contracts/ui-api.md "The shell"; B.3; research §1.7).
 * The chip shows `balance`; the run history stat row adds `reserved` (10/2).
 *
 * Mission 9 WP11 T065 (FR-011, FR-012, FR-015; amendment 6; research §1.19, §1.20; squad U17, U20):
 * - `billing` is the additive access member. It is optional: an absent member reads as `active`
 *   (`useBillingGate`), so every mission 3 fixture that stubs only the balance stays valid; the
 *   server is the control.
 * - The mission 3 cycle members are typed, optional for the same reason; the soft-cap warning reads
 *   them from this read, never from `GET /v1/billing` (U20).
 * - The ledger rows gain `block_id`, `cycle_start` and, on reservations, `allocation`; with
 *   `include=free` the page merges the two free kinds at delta 0, paged by an opaque cursor.
 */
import { api } from '../api'
import type { BillingAccess } from './billing'

export interface Credits {
  balance: number
  reserved: number
  allowance?: number
  cycle_start?: string | null
  cycle_end?: string | null
  first_cycle?: boolean
  forgiveness_used?: number
  forgiveness_remaining?: number
  billing?: BillingAccess
}

/**
 * The ledger_kind values (migration 0003) and the free kinds of `include=free` (research §1.20): a rejected
 * upload, a resolved transmission, and a Path A run's free analysis (mission 9 WP09, FR-012; WP17 renders it).
 */
export type LedgerKind =
  | 'cycle_grant'
  | 'purchase'
  | 'reservation'
  | 'release'
  | 'consumption'
  | 'expiry'
  | 'forgiveness'
  | 'free_upload'
  | 'free_transmission'
  | 'free_analysis'

/** One source of a reservation (research §1.7). */
export interface LedgerAllocation {
  source: 'included' | 'block'
  block_id: string | null
  cycle_start: string
  credits: number
}

export interface LedgerEntry {
  id: string
  kind: LedgerKind
  delta: number
  balance_after: number
  run_id: string | null
  note: string
  created_at: string
  block_id: string | null
  cycle_start: string | null
  allocation?: LedgerAllocation[]
}

/** A ledger page, newest first; `next_cursor` is opaque and belongs to its mode. */
export interface LedgerPage {
  entries: LedgerEntry[]
  next_cursor?: string
}

export interface LedgerParams {
  include?: 'free'
  cursor?: string | undefined
}

export async function getCredits(): Promise<Credits> {
  return api<Credits>('/credits')
}

/** GET /v1/credits/ledger, 50 entries per page (the server's page size). */
export async function getLedger({ include, cursor }: LedgerParams): Promise<LedgerPage> {
  const query = new URLSearchParams()
  if (include !== undefined) query.set('include', include)
  if (cursor !== undefined) query.set('cursor', cursor)
  const search = query.toString()
  return api<LedgerPage>(search === '' ? '/credits/ledger' : `/credits/ledger?${search}`)
}
